import { useRef, type ChangeEvent } from 'react';
import { Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button, Avatar } from '@/shared/ui';
import { toMessage } from '@/shared/lib/repository/errors';
import { useRemoveProfilePicture, useUploadProfilePicture } from '../api/useProfile';
import type { Profile } from '../model/types';

const MAX_PROFILE_PICTURE_SIZE = 2 * 1024 * 1024;
const ACCEPTED_PROFILE_PICTURE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

export function ProfilePictureField({ profile }: { profile: Profile }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const uploadPicture = useUploadProfilePicture();
  const removePicture = useRemoveProfilePicture();
  const isPending = uploadPicture.isPending || removePicture.isPending;

  const onFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_PROFILE_PICTURE_TYPES.has(file.type)) {
      toast.error('Choose a JPG, PNG, or WebP image');
      event.target.value = '';
      return;
    }
    if (file.size > MAX_PROFILE_PICTURE_SIZE) {
      toast.error('Profile pictures must be 2 MB or smaller');
      event.target.value = '';
      return;
    }

    try {
      await uploadPicture.mutateAsync({ file, previousUrl: profile.avatarUrl });
      toast.success('Profile picture updated');
    } catch (err) {
      toast.error(toMessage(err, 'Could not upload your profile picture'));
    } finally {
      event.target.value = '';
    }
  };

  const onRemove = async () => {
    try {
      await removePicture.mutateAsync(profile.avatarUrl);
      toast.success('Profile picture removed');
    } catch (err) {
      toast.error(toMessage(err, 'Could not remove your profile picture'));
    }
  };

  return (
    <div className="border-border flex flex-col gap-4 rounded-xl border p-4 sm:flex-row sm:items-center">
      <Avatar
        initials={profile.initials}
        color={profile.avatarColor}
        src={profile.avatarUrl}
        alt={`${profile.name}'s profile picture`}
        size="xl"
      />

      <div className="min-w-0 flex-1">
        <p className="text-foreground font-medium">Profile picture</p>
        <p className="text-muted-foreground mt-1 text-sm">JPG, PNG, or WebP. Maximum 2 MB.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="sr-only"
            onChange={onFileChange}
            disabled={isPending}
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={isPending}
            onClick={() => inputRef.current?.click()}
          >
            <Upload className="h-4 w-4" aria-hidden />
            {uploadPicture.isPending ? 'Uploading…' : profile.avatarUrl ? 'Replace' : 'Upload'}
          </Button>
          {profile.avatarUrl && (
            <Button type="button" variant="ghost" size="sm" disabled={isPending} onClick={onRemove}>
              <Trash2 className="h-4 w-4" aria-hidden />
              {removePicture.isPending ? 'Removing…' : 'Remove'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
